import SlIcon from '@shoelace-style/shoelace/components/icon/icon.component';
import {SlButton} from '@shoelace-style/shoelace/shoelace';
import {Translator} from '../../common/translator';

export default class MainNavigation {
    private readonly navElement: HTMLElement | null;
    private readonly translator: Translator;
    private onMenuOpenCallback: (() => void) | null = null;

    constructor() {
        this.navElement = document.getElementById('main_nav');
        this.translator = new Translator();

        if (this.navElement) {
            this.initListeners();
        }

        document.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                this.closeAllMenus();
            }
        });

        document.addEventListener('click', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (this.navElement && !this.navElement.contains(target)) {
                this.closeAllMenus();
            }
        });
    }

    public onMenuOpen(callback: () => void): void {
        this.onMenuOpenCallback = callback;
    }

    private initListeners(): void {
        const listItems = this.navElement!.querySelectorAll('li');

        listItems.forEach((li) => {
            const toggleButton = li.querySelector<SlButton>('sl-button.submenu-toggle');
            const submenu = li.querySelector<HTMLElement>('.subnav-container');

            if (toggleButton && submenu) {
                toggleButton.addEventListener('click', () => {
                    const isOpen = toggleButton.getAttribute('aria-expanded') === 'true';
                    if (isOpen) {
                        this.deactivateItem(toggleButton, submenu, li);
                    } else {
                        this.openExclusiveItem(toggleButton, submenu, li);
                    }
                });

                toggleButton.addEventListener('keydown', (e: KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ' || e.code === 'Space') {
                        e.preventDefault();
                        const isOpen = toggleButton.getAttribute('aria-expanded') === 'true';
                        if (isOpen) {
                            this.deactivateItem(toggleButton, submenu, li);
                        } else {
                            this.openExclusiveItem(toggleButton, submenu, li);
                        }
                    } else if (e.key === 'Escape') {
                        this.deactivateItem(toggleButton, submenu, li);
                        toggleButton.focus();
                    }
                });

                li.addEventListener('focusout', () => {
                    setTimeout(() => {
                        const newlyFocused = document.activeElement;
                        if (!li.contains(newlyFocused)) {
                            if (li.classList.contains('open')) {
                                this.deactivateItem(toggleButton, submenu, li);
                            }
                        }
                    }, 0);
                });

                li.addEventListener('mouseenter', () => {
                    this.openExclusiveItem(toggleButton, submenu, li);
                });

                li.addEventListener('mouseleave', () => {
                    this.deactivateItem(toggleButton, submenu, li);
                });
            }
        });
    }

    private openExclusiveItem(button: SlButton, container: HTMLElement, li: HTMLElement): void {
        if (this.onMenuOpenCallback) {
            this.onMenuOpenCallback();
        }

        this.closeOtherMenus(li);
        this.activateItem(button, container, li);
    }

    private closeOtherMenus(currentLi: Element): void {
        const listItems = this.navElement!.querySelectorAll('li');

        listItems.forEach((li) => {
            if (li !== currentLi && li.classList.contains('open')) {
                const toggleButton = li.querySelector<SlButton>('sl-button.submenu-toggle');
                const submenu = li.querySelector<HTMLElement>('.subnav-container');
                if (toggleButton && submenu) {
                    this.deactivateItem(toggleButton, submenu, li);
                }
            }
        });
    }

    private activateItem(button: SlButton, container: HTMLElement, li: HTMLElement): void {
        button.setAttribute('aria-expanded', 'true');
        const icon = button.querySelector<SlIcon>('sl-icon');
        if (icon) {
            icon.setAttribute('name', 'chevron-up');
        }
        this.setSrText(button, 'nav-close');
        container.classList.add('open');
        li.classList.add('open');
    }

    private deactivateItem(button: SlButton, container: HTMLElement, li: HTMLElement): void {
        button.setAttribute('aria-expanded', 'false');
        const icon = button.querySelector<SlIcon>('sl-icon');
        if (icon) {
            icon.setAttribute('name', 'chevron-down');
        }
        this.setSrText(button, 'nav-open');
        container.classList.remove('open');
        li.classList.remove('open');
    }

    public closeAllMenus(): void {
        const listItems = this.navElement!.querySelectorAll('li');

        listItems.forEach((li) => {
            const toggleButton = li.querySelector<SlButton>('sl-button.submenu-toggle');
            const submenu = li.querySelector<HTMLElement>('.subnav-container');

            if (toggleButton && submenu && li.classList.contains('open')) {
                this.deactivateItem(toggleButton, submenu, li);
            }
        });
    }

    private setSrText(button: HTMLElement, state: 'nav-open' | 'nav-close'): void {
        const srSpan = button.querySelector<HTMLElement>('.sr-only');
        if (!srSpan) return;

        try {
            srSpan.textContent = this.translator.translateText(button, state);
        } catch (e) {
            // eslint-disable-next-line preserve-caught-error
            throw new Error('Translation failed: ' + (e as Error).message);
        }
    }
}
